import { useParams, Link } from 'react-router-dom';
import { sportArticles, sportCategories } from '@/mocks/sportsData';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = sportArticles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = sportArticles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);

  const categoryLabelMap: Record<string, string> = {
    football: '足球',
    basketball: '篮球',
    tennis: '网球',
    esports: '电子竞技',
    baseball: '棒球',
    racing: '赛车',
  };

  const categoryLabel = categoryLabelMap[article.category] || article.category;
  const category = sportCategories.find((c) => c.slug === article.category);
  const categoryIcon = category ? category.icon : 'ri-trophy-line';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '开体育在线',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.kaitiyu.com/article/${article.id}`}
        ogType="article"
        articleData={{
          publishedTime: article.publishDate,
          author: article.author,
          tags: article.tags,
        }}
      />
      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: categoryLabel, href: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <main className="pt-4">
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="flex items-center gap-1 text-xs font-medium bg-primary-100 text-primary-700 px-3 py-1 rounded-full hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className={`${categoryIcon}`}></i>
                  {categoryLabel}
                </Link>
                {article.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 border-b border-background-200/70 pb-5 flex-wrap">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                <span><i className="ri-eye-line mr-1"></i>{article.readCount.toLocaleString()} 阅读</span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1.5 rounded-full border border-background-200/70">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-[860px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <style>{`
        .prose-custom h2 { font-size: 1.35rem; font-weight: 700; margin-top: 2rem; margin-bottom: 0.75rem; color: oklch(var(--foreground-900)); font-family: var(--font-heading); }
        .prose-custom h3 { font-size: 1.15rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; color: oklch(var(--foreground-800)); font-family: var(--font-heading); }
        .prose-custom p { margin-bottom: 1rem; font-size: 0.95rem; line-height: 1.85; }
        .prose-custom strong { font-weight: 600; color: oklch(var(--foreground-900)); }
      `}</style>

      <Footer />
    </>
  );
}